import { useRef, type ReactNode } from 'react';
import { Spinner, makeStyles, mergeClasses } from '@fluentui/react-components';
import { Add16Regular, Grid16Regular } from '@fluentui/react-icons';
import { estadoDelPlano, tipoDePlanoPorNombre, type EstadoDelPlano } from '@supernova/shared';
import type { PlanoGaleriaItem } from '../../api/planos.js';
import { brandingProductoActivo } from '../../lib/brandingDelProducto.js';
import { MiniaturaPlano } from './MiniaturaPlano.js';

/**
 * TIRA DE PLANOS arriba del visor, como las pestañas de archivo de AutoCAD: "Todos los planos"
 * (vuelve a la galería), una pestaña por plano con su mini vista previa, nombre y punto de estado,
 * y "+" para subir otro. La pestaña activa lleva la línea superior del color de la marca. A la
 * derecha, lo que el padre ponga (el papel del plano, la ayuda, BETA).
 */

const useStyles = makeStyles({
  tira: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '2px',
    minHeight: '44px',
    padding: '0 12px',
    background: 'var(--bg-deep)',
    borderBottom: '1px solid var(--border-subtle)',
    overflow: 'hidden',
  },
  pestanas: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '2px',
    minWidth: 0,
    flex: '1 1 auto',
    overflowX: 'auto',
    scrollbarWidth: 'thin',
  },
  pestana: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    maxWidth: '240px',
    padding: '0 12px',
    border: 'none',
    borderTop: '2px solid transparent',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontFamily: 'inherit',
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
  },
  activa: { background: 'var(--bg-page)', color: 'var(--text-primary)' },
  mini: { width: '36px', borderRadius: '4px', border: '1px solid var(--border-subtle)', flexShrink: 0 },
  nombre: { overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 },
  punto: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  mas: { width: '36px', justifyContent: 'center', padding: 0 },
  derecha: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, paddingLeft: '12px' },
});

function colorDeEstado(e: EstadoDelPlano): string {
  if (e === 'listo') return 'var(--ok)';
  if (e === 'en_curso') return 'var(--warn)';
  return 'var(--text-tertiary)';
}

interface Props {
  planos: PlanoGaleriaItem[];
  activoId: string | null;
  onTodos: () => void;
  onElegir: (fileId: string) => void;
  /** Sin esto no hay "+" (solo lectura). */
  onSubir?: (files: File[]) => Promise<void>;
  subiendo?: boolean;
  derecha?: ReactNode;
}

export function TiraDePlanos({ planos, activoId, onTodos, onElegir, onSubir, subiendo, derecha }: Props) {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const marca = brandingProductoActivo();

  return (
    <div className={styles.tira} role="tablist" aria-label="Planos del proyecto">
      <div className={styles.pestanas}>
        <button type="button" className={styles.pestana} onClick={onTodos} title="Volver a la galería de planos">
          <Grid16Regular />
          Todos los planos
        </button>
        {planos.map((p) => {
          const activa = p.fileId === activoId;
          const est = estadoDelPlano(p.renglones, p.avance);
          return (
            <button
              key={p.fileId}
              type="button"
              role="tab"
              aria-selected={activa}
              className={mergeClasses(styles.pestana, activa && styles.activa)}
              // El magenta (o el color del producto) es solo marca: la pestaña del plano abierto.
              style={activa ? { borderTopColor: marca.c1 } : undefined}
              onClick={() => !activa && onElegir(p.fileId)}
              title={`${p.nombre} · ${est.texto}`}
            >
              <MiniaturaPlano plano={p} chica className={styles.mini} />
              <span className={styles.nombre}>{p.nombre}</span>
              <span className={styles.punto} style={{ background: colorDeEstado(est.estado) }} aria-label={est.texto} />
            </button>
          );
        })}
        {onSubir && (
          <button
            type="button"
            className={mergeClasses(styles.pestana, styles.mas)}
            onClick={() => !subiendo && inputRef.current?.click()}
            title="Subir otro plano (IFC, DWG o DXF se miden; de un PDF solo se copia lo escrito)"
            aria-label="Subir otro plano"
          >
            {subiendo ? <Spinner size="extra-tiny" /> : <Add16Regular />}
          </button>
        )}
      </div>
      {derecha && <div className={styles.derecha}>{derecha}</div>}
      {onSubir && (
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".ifc,.dwg,.dxf,.pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const fs = Array.from(e.target.files ?? []).filter((f) => tipoDePlanoPorNombre(f.name) != null);
            if (inputRef.current) inputRef.current.value = '';
            if (fs.length > 0) void onSubir(fs);
          }}
        />
      )}
    </div>
  );
}
